import axios from 'axios';

//const API_URL = 'http://localhost:5000/api';
const RAW_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// normalize: remove trailing slashes
const API_BASE = RAW_BASE.replace(/\/+$/, '');

// if env already includes /api, don't append it again
const API_URL = API_BASE.toLowerCase().endsWith('/api')
  ? API_BASE
  : `${API_BASE}/api`;

// --- Session timeout helper (idle logout) ---
const SESSION_TIMEOUT_CODE = 'SESSION_IDLE_TIMEOUT';

// Guard to avoid installing interceptors multiple times
let _sessionTimeoutInterceptorAttached = false;

function handleSessionTimeout() {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  } catch {
    // ignore storage issues
  }

  // Redirect to login with a reason so UI can show a message
  if (window.location.pathname !== '/login') {
    window.location.href = '/login?reason=timeout';
  }
}

// Attach a single response interceptor to catch idle timeout across the app
export function attachSessionTimeoutInterceptor(apiInstance) {
  if (_sessionTimeoutInterceptorAttached) return;
  _sessionTimeoutInterceptorAttached = true;

  apiInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      if (status === 401 && code === SESSION_TIMEOUT_CODE) {
        handleSessionTimeout();
      }

      return Promise.reject(error);
    }
  );
}

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true
});

// ✅ install idle-timeout handler once
attachSessionTimeoutInterceptor(api);

// Add token to requests if it exists
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Auth endpoints
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  getProfile: () => api.get('/auth/profile'),
  updateRole: (role) => api.put('/auth/role', { role }),
  verifyEmail: (token) =>
    api.get(`/auth/verify-email?token=${encodeURIComponent(token)}`),
  resendVerification: (email) =>
    api.post('/auth/resend-verification', { email }),
};

// Documents endpoints (for Source Material)
export const documentsAPI = {
  // Player-facing
  getCategories: () => api.get('/documents/categories'),
  getDocumentsByCategory: (category) =>
    api.get(`/documents/category/${encodeURIComponent(category)}`),
  getDocument: (id) => api.get(`/documents/${id}`),

  // Admin-facing
  adminList: () => api.get('/documents/admin'),
  adminGetCategories: () => api.get('/documents/admin/categories'),
  adminGetDocument: (id) => api.get(`/documents/admin/${id}`),
  adminCreate: (payload) => api.post('/documents/admin', payload),
  adminUpdate: (id, payload) => api.put(`/documents/admin/${id}`, payload),
  adminDelete: (id) => api.delete(`/documents/admin/${id}`),
  adminRenameCategory: (oldCategory, newCategory) =>
    api.put('/documents/admin/category/rename', { oldCategory, newCategory }),
};

// News endpoints
export const newsAPI = {
  getAllNews: (params) => api.get('/news', { params }), // params can include: category, limit, offset
  getNewsByCategory: (category, params) => api.get(`/news/category/${category}`, { params }),
  getNewsById: (id) => api.get(`/news/${id}`),
  createNews: (newsData) => api.post('/news', newsData),
  updateNews: (id, newsData) => api.put(`/news/${id}`, newsData),
  deleteNews: (id) => api.delete(`/news/${id}`)
};

// Feedback endpoints
export const feedbackAPI = {
  // User endpoints
  submit: (feedbackData) => api.post('/feedback', feedbackData),
  getMyFeedback: () => api.get('/feedback/my-feedback'),

  // Admin endpoints
  getAll: (params) => api.get('/feedback', { params }), // params: status, limit, offset
  getStats: () => api.get('/feedback/stats'),
  updateStatus: (id, updateData) => api.put(`/feedback/${id}`, updateData),
  delete: (id) => api.delete(`/feedback/${id}`)
};

// Campaign endpoints
export const campaignsAPI = {
  getMyCampaigns: () => api.get('/campaigns'),
  createCampaign: (campaignData) => api.post('/campaigns', campaignData),
  getCampaignDetails: (id) => api.get(`/campaigns/${id}`),
  updateCampaign: (id, campaignData) => api.put(`/campaigns/${id}`, campaignData),
  getCampaignPlayers: (campaignId) => api.get(`/campaigns/${campaignId}/players`),
  getCharacterSheet: (characterId) => api.get(`/campaigns/characters/${characterId}`),
  awardXp: (campaignId, playerId, amount, reason) =>
    api.post(`/campaigns/${campaignId}/award-xp`, { playerId, amount, reason }),
  awardXpToAll: (campaignId, amount, reason) =>
    api.post(`/campaigns/${campaignId}/award-xp-all`, { amount, reason }),
  getCampaignNpcs: (campaignId) => api.get(`/campaigns/${campaignId}/npcs`),
  getNpcDetails: (npcId) => api.get(`/campaigns/npcs/${npcId}`),
  createNpc: (campaignId, npcData) => api.post(`/campaigns/${campaignId}/npcs`, npcData),
  deleteNpc: (campaignId, npcId) => api.delete(`/campaigns/${campaignId}/npcs/${npcId}`),
  grantNpcXp: (campaignId, npcId, amount) =>
    api.post(`/campaigns/${campaignId}/npcs/${npcId}/grant-xp`, { amount }),
  leaveCampaign: (campaignId) => api.delete(`/campaigns/${campaignId}/leave`)
};

// Live Session endpoints (real-time play)
export const liveSessionsAPI = {
  // GM: create or return active live session for campaign
  launch: (campaignId) => api.post(`/live/campaigns/${campaignId}/launch`),

  // Player/GM: fetch the active session (if any)
  getActive: (campaignId) => api.get(`/live/campaigns/${campaignId}/active`),

  // Player/GM: join a live session (optionally bind a character)
  join: (sessionId, characterId = null) => api.post(`/live/sessions/${sessionId}/join`, { characterId }),

  // GM: end the session
  end: (sessionId) => api.post(`/live/sessions/${sessionId}/end`),

  // Optional: participants list
  getParticipants: (sessionId) => api.get(`/live/sessions/${sessionId}/participants`)
};

// Invitation endpoints
export const invitationsAPI = {
  getMyInvitations: () => api.get('/invitations/my-invitations'),
  invitePlayer: (campaignId, invitationData) => api.post(`/invitations/campaigns/${campaignId}/invite`, invitationData),
  respondToInvitation: (invitationId, action) => api.put(`/invitations/${invitationId}/respond`, { action }),
  getCampaignInvitations: (campaignId) => api.get(`/invitations/campaigns/${campaignId}/invitations`),
  revokeInvitation: (invitationId) => api.delete(`/invitations/${invitationId}/revoke`),
  attachExistingCharacterToCampaign: (campaignId, characterId) =>
    api.post(`/invitations/campaigns/${campaignId}/attach-character`, { characterId })
};

// Character endpoints
export const charactersAPI = {
  getMyCharacters: () => api.get('/characters'),
  createCharacter: (characterData) => api.post('/characters', characterData),
  getCharacter: (id) => api.get(`/characters/${id}`),
  updateCharacter: (id, characterData) => api.put(`/characters/${id}`, characterData),
  deleteCharacter: (id) => api.delete(`/characters/${id}`)
};

export const adminAPI = {
  // User Management
  getAllUsers: (params) => api.get('/admin/users', { params }), // params: page, limit, role, search, sortBy, sortOrder
  getUserStats: () => api.get('/admin/users/stats'),
  getUserById: (userId) => api.get(`/admin/users/${userId}`),
  updateUserRole: (userId, role) => api.put(`/admin/users/${userId}/role`, { role }),
  toggleEmailVerification: (userId) => api.put(`/admin/users/${userId}/verify-email`),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  searchUsers: (query, limit = 10) => api.get('/admin/users/search', { params: { query, limit } })
};

export const abilitiesAPI = {
  getAll: () => api.get('/abilities/all'),

  getByStat: (stat, options = {}) => {
    const params = new URLSearchParams();
    if (options.characterStat) params.append('characterStat', options.characterStat);
    if (options.availableXP !== undefined) params.append('availableXP', options.availableXP);
    // ownedAbilities no longer used server-side (v2 supports upgrades), but keep it harmless if passed
    if (options.ownedAbilities) params.append('ownedAbilities', options.ownedAbilities);

    return api.get(`/abilities/by-stat/${stat}?${params.toString()}`);
  },

  search: (query) => api.get(`/abilities/search?query=${encodeURIComponent(query)}`),

  getAffordable: (availableXP, ownedAbilities = '') =>
    api.get(`/abilities/affordable?availableXP=${availableXP}&ownedAbilities=${encodeURIComponent(ownedAbilities)}`),
};

export const skillsAPI = {
  getTiers: () => api.get('/skills/tiers')
};

// Session API functions
export const sessionsAPI = {
  getCampaignSessions: (campaignId) =>
    api.get(`/campaigns/${campaignId}/sessions`),

  getSession: (sessionId) =>
    api.get(`/campaigns/sessions/${sessionId}`),

  createSession: (campaignId, sessionData) =>
    api.post(`/campaigns/${campaignId}/sessions`, sessionData),

  updateSession: (sessionId, sessionData) =>
    api.put(`/campaigns/sessions/${sessionId}`, sessionData),

  deleteSession: (sessionId) =>
    api.delete(`/campaigns/sessions/${sessionId}`)
};

// Map API functions
export const mapsAPI = {
  getCampaignMaps: (campaignId) =>
    api.get(`/campaigns/${campaignId}/maps`),

  createCampaignMap: (campaignId, mapData) =>
    api.post(`/campaigns/${campaignId}/maps`, mapData),

  updateCampaignMap: (campaignId, mapId, mapData) =>
    api.put(`/campaigns/${campaignId}/maps/${mapId}`, mapData),

  deleteCampaignMap: (campaignId, mapId) =>
    api.delete(`/campaigns/${campaignId}/maps/${mapId}`)
};

export default api;
